/*
 * Sticky boolean flag: `?<param>=1` enables, `=0` disables, and the value
 * persists so the choice survives navigations and later sessions without the
 * query param.
 *
 * Ported from arbook-lti-frontend (AT2-19905) with localStorage in place of
 * sessionStorage: arbook is a top-level page, so a flag set once stays set,
 * whereas LTI runs in a third-party LMS iframe where persistent storage is
 * denied and a flag only lasts the launch session.
 */

/*
 * Resolved once per key. The URL is read only to WRITE storage; every later read
 * comes from the memo, because getAppLocale() runs on every Apollo request
 * (core/apolloClient/apollo.ts) and must not re-parse the URL or re-write
 * storage there. No flag can change without a reload — setAppLocale reloads by
 * design.
 */
const resolvedFlags = new Map<string, boolean>();

const resolveStickyFlag = (urlParam: string, storageKey: string): boolean => {
  const fromUrl = new URLSearchParams(window.location.search).get(urlParam);

  try {
    if (fromUrl === "0" || fromUrl === "1") {
      localStorage.setItem(storageKey, fromUrl);

      return fromUrl === "1";
    }

    return localStorage.getItem(storageKey) === "1";
  } catch {
    // Storage denied (private mode, blocked cookies): honour the URL param for
    // this load only, with no way to persist it.
    return fromUrl === "1";
  }
};

export const readStickyFlag = (
  urlParam: string,
  storageKey: string,
): boolean => {
  const cachedFlag = resolvedFlags.get(storageKey);

  if (cachedFlag !== undefined) {
    return cachedFlag;
  }

  const resolvedFlag = resolveStickyFlag(urlParam, storageKey);
  resolvedFlags.set(storageKey, resolvedFlag);

  return resolvedFlag;
};
